<template>
  <div class="flex-1 overflow-auto p-4">
    <!-- AI F §1.1.6 — warning from 80% of the quota, blocking at 100% -->
    <div
      v-if="storage.percentage >= 80"
      class="mb-4 rounded-md px-4 py-3 text-sm"
      :class="
        storage.percentage >= 100
          ? 'bg-red-50 text-red-800'
          : 'bg-yellow-50 text-yellow-800'
      "
    >
      {{
        storage.percentage >= 100
          ? $t('KNOWLEDGE_BASE.STORAGE.FULL')
          : $t('KNOWLEDGE_BASE.STORAGE.ALMOST_FULL')
      }}
      {{
        $t('KNOWLEDGE_BASE.STORAGE.USAGE', {
          used: formatSize(storage.used),
          quota: formatSize(storage.quota),
        })
      }}
    </div>

    <div class="button--fixed-top gap-2">
      <woot-button
        v-if="hasEditPermission('Knowledge Base')"
        icon="search"
        color-scheme="success"
        @click="showTestQuery = true"
      >
        {{ $t('KNOWLEDGE_BASE.TEST_QUERY.BUTTON') }}
      </woot-button>
      <!-- AI F §1.1.6 — adding is blocked at 100% of the quota -->
      <woot-button
        v-if="hasCreatePermission('Knowledge Base')"
        v-tooltip.top="
          storage.percentage >= 100 ? $t('KNOWLEDGE_BASE.STORAGE.FULL') : ''
        "
        icon="arrow-upload"
        color-scheme="secondary"
        :is-disabled="storage.percentage >= 100"
        @click="showUpload = true"
      >
        {{ $t('KNOWLEDGE_BASE.UPLOAD.BUTTON') }}
      </woot-button>
      <!-- AI F §1.3.2 — adding a website URL -->
      <woot-button
        v-if="hasCreatePermission('Knowledge Base')"
        v-tooltip.top="
          storage.percentage >= 100 ? $t('KNOWLEDGE_BASE.STORAGE.FULL') : ''
        "
        icon="link"
        color-scheme="secondary"
        :is-disabled="storage.percentage >= 100"
        @click="showAddUrl = true"
      >
        {{ $t('KNOWLEDGE_BASE.URL.BUTTON') }}
      </woot-button>
      <!-- AI F §1.4.2 — a live read of one page, kept only for a day -->
      <woot-button
        v-if="hasCreatePermission('Knowledge Base')"
        v-tooltip.top="
          storage.percentage >= 100 ? $t('KNOWLEDGE_BASE.STORAGE.FULL') : ''
        "
        icon="globe-search"
        color-scheme="secondary"
        :is-disabled="storage.percentage >= 100"
        @click="showWebSearch = true"
      >
        {{ $t('KNOWLEDGE_BASE.WEB_SEARCH.BUTTON') }}
      </woot-button>
      <woot-button
        v-if="hasCreatePermission('Knowledge Base')"
        v-tooltip.top="
          storage.percentage >= 100 ? $t('KNOWLEDGE_BASE.STORAGE.FULL') : ''
        "
        icon="add-circle"
        color-scheme="secondary"
        :is-disabled="storage.percentage >= 100"
        @click="openForm(null)"
      >
        {{ $t('KNOWLEDGE_BASE.CUSTOM_ANSWER.ADD_BUTTON') }}
      </woot-button>
    </div>

    <div class="flex flex-row items-center gap-3 mb-4">
      <select v-model="typeFilter" class="!mb-0 w-48" @change="applyFilters">
        <option value="">{{ $t('KNOWLEDGE_BASE.FILTERS.ALL_TYPES') }}</option>
        <option v-for="type in sourceTypes" :key="type" :value="type">
          {{ $t(`KNOWLEDGE_BASE.TYPES.${type.toUpperCase()}`) }}
        </option>
      </select>
      <select v-model="statusFilter" class="!mb-0 w-48" @change="applyFilters">
        <option value="">
          {{ $t('KNOWLEDGE_BASE.FILTERS.ALL_STATUSES') }}
        </option>
        <option v-for="status in statuses" :key="status" :value="status">
          {{ $t(`KNOWLEDGE_BASE.STATUSES.${status.toUpperCase()}`) }}
        </option>
      </select>
      <!-- AI F §1.1.5 — filters by Last Updated and Size. -->
      <date-picker
        v-model="updatedRange"
        type="date"
        range
        format="DD-MM-YYYY"
        value-type="YYYY-MM-DD"
        class="w-64"
        input-class="!mb-0"
        :placeholder="$t('KNOWLEDGE_BASE.FILTERS.UPDATED_RANGE')"
        @change="applyFilters"
      />
      <!-- AI F §1.1.5 — Size as a range, in the megabytes the Size column shows -->
      <div class="flex flex-row items-center gap-1">
        <input
          v-model="sizeFrom"
          type="number"
          min="0"
          step="0.1"
          class="!mb-0 w-32"
          :placeholder="$t('KNOWLEDGE_BASE.FILTERS.SIZE_FROM')"
          @change="applyFilters"
        />
        <span class="text-slate-500">—</span>
        <input
          v-model="sizeTo"
          type="number"
          min="0"
          step="0.1"
          class="!mb-0 w-32"
          :placeholder="$t('KNOWLEDGE_BASE.FILTERS.SIZE_TO')"
          @change="applyFilters"
        />
      </div>
    </div>

    <woot-loading-state
      v-if="uiFlags.isFetching"
      :message="$t('KNOWLEDGE_BASE.LOADING')"
    />
    <p
      v-else-if="!records.length"
      class="flex items-center flex-col justify-center py-10 text-slate-500"
    >
      {{ $t('KNOWLEDGE_BASE.LIST.404') }}
    </p>
    <table v-else class="woot-table">
      <thead>
        <th
          v-for="column in columns"
          :key="column.label"
          :class="{ 'cursor-pointer select-none': column.key }"
          @click="toggleSort(column.key)"
        >
          {{ $t(`KNOWLEDGE_BASE.LIST.TABLE_HEADER.${column.label}`) }}
          <span v-if="column.key && sortConfig.key === column.key">
            {{ sortConfig.order === 'asc' ? '↑' : '↓' }}
          </span>
        </th>
      </thead>
      <tbody>
        <tr
          v-for="source in records"
          :key="source.id"
          class="cursor-pointer"
          @click="openDetails(source)"
        >
          <td>{{ source.name }}</td>
          <td>
            {{ $t(`KNOWLEDGE_BASE.TYPES.${source.source_type.toUpperCase()}`) }}
          </td>
          <td>
            <status-badge :status="source.status" />
          </td>
          <td>{{ formatTime(source.content_updated_at) }}</td>
          <td>{{ formatSize(source.size_bytes) }}</td>
          <td>{{ source.created_by ? source.created_by.name : '—' }}</td>
        </tr>
      </tbody>
    </table>

    <table-footer
      v-if="records.length"
      :current-page="Number(meta.current_page)"
      :total-count="meta.count"
      :page-size="25"
      @page-change="page => fetch({ page })"
    />

    <!-- AI F §1.2.5 — the key rebuilds the panel on a different source: its extracted text lives in data() -->
    <source-details
      v-if="selectedSource"
      :key="selectedSource.id"
      :source="selectedSource"
      @close="clearSelected"
      @edit="openForm(selectedSource)"
      @reindex="reindex"
      @reextract="showReextract = true"
      @save-content="saveContent"
      @archive="archive"
      @restore="restore"
    />
    <woot-modal
      :show.sync="showTestQuery"
      :on-close="() => (showTestQuery = false)"
    >
      <test-query v-if="showTestQuery" @close="showTestQuery = false" />
    </woot-modal>
    <woot-modal :show.sync="showForm" :on-close="closeForm">
      <custom-answer-form
        v-if="showForm"
        :source="editedSource"
        @close="closeForm"
        @saved="closeForm"
      />
    </woot-modal>
    <!-- AI F §1.2.5, §1.3.6, §1.4.4 — Edit metadata -->
    <woot-modal :show.sync="showRename" :on-close="closeRename">
      <rename-source
        v-if="showRename"
        :source="editedSource"
        @close="closeRename"
      />
    </woot-modal>
    <!-- AI F §1.1.2, §1.2.3 — a fresh extraction throws away corrections made by hand (§1.2.5) -->
    <woot-modal
      :show.sync="showReextract"
      :on-close="() => (showReextract = false)"
    >
      <div class="p-8">
        <woot-modal-header
          :header-title="$t('KNOWLEDGE_BASE.DETAILS.REEXTRACT_CONFIRM.TITLE')"
          :header-content="
            $t('KNOWLEDGE_BASE.DETAILS.REEXTRACT_CONFIRM.MESSAGE')
          "
        />
        <div class="flex justify-end gap-2 mt-4">
          <woot-button variant="clear" @click="showReextract = false">
            {{ $t('KNOWLEDGE_BASE.DETAILS.REEXTRACT_CONFIRM.NO') }}
          </woot-button>
          <woot-button color-scheme="alert" @click="reextract">
            {{ $t('KNOWLEDGE_BASE.DETAILS.REEXTRACT_CONFIRM.YES') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>
    <woot-modal :show.sync="showUpload" :on-close="() => (showUpload = false)">
      <upload-files v-if="showUpload" @close="showUpload = false" />
    </woot-modal>
    <woot-modal :show.sync="showAddUrl" :on-close="() => (showAddUrl = false)">
      <add-url v-if="showAddUrl" @close="showAddUrl = false" />
    </woot-modal>
    <woot-modal
      :show.sync="showWebSearch"
      :on-close="() => (showWebSearch = false)"
    >
      <web-search-form v-if="showWebSearch" @close="showWebSearch = false" />
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import { formatSize } from './helpers';
import TableFooter from 'dashboard/components/widgets/TableFooter';
import timeMixin from '../../../../mixins/time';
import hasPermission from '../../../../mixins/hasPermission';
import StatusBadge from './StatusBadge';
import SourceDetails from './SourceDetails';
import TestQuery from './TestQuery';
import CustomAnswerForm from './CustomAnswerForm';
import UploadFiles from './UploadFiles';
import AddUrl from './AddUrl';
import WebSearchForm from './WebSearchForm';
import RenameSource from './RenameSource';
import DatePicker from 'vue2-datepicker';

export default {
  components: {
    TableFooter,
    StatusBadge,
    SourceDetails,
    TestQuery,
    CustomAnswerForm,
    UploadFiles,
    AddUrl,
    WebSearchForm,
    RenameSource,
    DatePicker,
  },
  mixins: [timeMixin, hasPermission, alertMixin],
  data() {
    return {
      typeFilter: '',
      statusFilter: '',
      // AI F §1.1.5 — [from, to] as YYYY-MM-DD, either end may stay empty.
      updatedRange: [],
      sizeFrom: '',
      sizeTo: '',
      showTestQuery: false,
      showForm: false,
      showRename: false,
      showUpload: false,
      showAddUrl: false,
      showWebSearch: false,
      showReextract: false,
      editedSource: null,
      // AI F §1.1.5 — Name, Type, Status, Last Updated, Size, Created By
      columns: [
        { key: 'name', label: 'NAME' },
        { key: 'source_type', label: 'TYPE' },
        { key: 'status', label: 'STATUS' },
        { key: 'content_updated_at', label: 'LAST_UPDATED' },
        { key: 'size_bytes', label: 'SIZE' },
        { key: null, label: 'CREATED_BY' },
      ],
      sourceTypes: ['file', 'website_url', 'web_search', 'custom_answer'],
      statuses: [
        'indexing',
        'searching',
        'active',
        'failed',
        'archived',
        'expired',
      ],
    };
  },
  computed: {
    ...mapGetters({
      records: 'kbSources/getRecords',
      meta: 'kbSources/getMeta',
      uiFlags: 'kbSources/getUIFlags',
      sortConfig: 'kbSources/getSortConfig',
      selectedSource: 'kbSources/getSelectedSource',
      storage: 'kbSources/getStorageUsage',
    }),
  },
  mounted() {
    this.fetch();
  },
  methods: {
    formatSize,
    fetch(params = {}) {
      this.$store.dispatch('kbSources/fetch', params);
    },
    applyFilters() {
      this.$store.dispatch('kbSources/setFilters', {
        status: this.statusFilter ? [this.statusFilter] : [],
        sourceType: this.typeFilter ? [this.typeFilter] : [],
        updatedFrom: this.updatedRange?.[0] || '',
        updatedTo: this.updatedRange?.[1] || '',
        sizeFrom: this.sizeFrom,
        sizeTo: this.sizeTo,
      });
    },
    toggleSort(key) {
      if (!key) return;
      const order =
        this.sortConfig.key === key && this.sortConfig.order === 'asc'
          ? 'desc'
          : 'asc';
      this.$store.dispatch('kbSources/setSort', { key, order });
    },
    openDetails(source) {
      this.$store.dispatch('kbSources/show', source.id);
    },
    // AI F §1.2.5, §1.5.1 — a null source means "new custom answer"
    openForm(source) {
      this.editedSource = source;
      if (!source || source.source_type === 'custom_answer')
        this.showForm = true;
      else this.showRename = true;
    },
    // AI F §1.2.5
    async reindex() {
      await this.$store.dispatch('kbSources/reindex', this.selectedSource.id);
      this.showAlert(this.$t('KNOWLEDGE_BASE.DETAILS.REINDEX_STARTED'));
    },
    // AI F §1.1.2, §1.2.3 — the stored original is read again and replaces the text, edits included
    async reextract() {
      this.showReextract = false;
      await this.$store.dispatch('kbSources/reextract', this.selectedSource.id);
      this.showAlert(this.$t('KNOWLEDGE_BASE.DETAILS.REEXTRACT_STARTED'));
    },
    // AI F §1.1.8 — archiving takes the source out of search; it can be restored for 30 days
    async archive() {
      await this.$store.dispatch('kbSources/archive', this.selectedSource.id);
      this.showAlert(this.$t('KNOWLEDGE_BASE.DETAILS.ARCHIVED'));
    },
    async restore() {
      await this.$store.dispatch('kbSources/restore', this.selectedSource.id);
      this.showAlert(this.$t('KNOWLEDGE_BASE.DETAILS.RESTORED'));
    },
    async saveContent(content) {
      await this.$store.dispatch('kbSources/update', {
        id: this.selectedSource.id,
        content,
      });
      this.showAlert(this.$t('KNOWLEDGE_BASE.DETAILS.CONTENT_SAVED'));
    },
    closeForm() {
      this.showForm = false;
      this.editedSource = null;
    },
    closeRename() {
      this.showRename = false;
      this.editedSource = null;
    },
    clearSelected() {
      this.$store.dispatch('kbSources/clearSelected');
    },
    formatTime(timestamp) {
      return timestamp
        ? this.messageStamp(timestamp, 'LLL d, yyyy h:mm a')
        : '—';
    },
  },
};
</script>
